import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Container,
    Grid,
    Typography
} from "@mui/material";
import { config } from "../Constants";
import Seo from "../components/Seo";
import { TRACK_HUBS } from "../content/trackHubs";
import { formatCurrency, formatPercent, getDisplayPrice } from "../utils/priceUtils";

const URL = config.url;
const SITE_URL = "https://stockmaid.com";

export const TrackHubPage = () => {
    const { trackSlug } = useParams();
    const hub = TRACK_HUBS[trackSlug];
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [stocks, setStocks] = useState([]);
    const [lastRefreshedAt, setLastRefreshedAt] = useState(null);

    useEffect(() => {
        if (!hub) {
            setLoading(false);
            setError("Tracker page not found.");
            return;
        }

        const loadStocks = async () => {
            setLoading(true);
            setError("");
            try {
                const params = new URLSearchParams({
                    size: "9999",
                    manufacturers: "NVIDIA",
                    searchTerm: hub.searchTerm
                });

                const response = await fetch(`${URL}/api/v1/stocks?${params.toString()}`);
                const payload = await response.json();
                const items = Array.isArray(payload?.data) ? payload.data : [];

                const filtered = items
                    .filter((item) => {
                        const name = (item?.name || "").toLowerCase();
                        const isSeriesMatch = name.includes(hub.series.toLowerCase());
                        if (!isSeriesMatch) {
                            return false;
                        }

                        if (hub.slug === "nvidia-rtx-5070" && name.includes("5070 ti")) {
                            return false;
                        }

                        return true;
                    })
                    .sort((a, b) => {
                        const availabilityA = (a.onlineAvailability ? 2 : 0) + (a.inStoreAvailability ? 1 : 0);
                        const availabilityB = (b.onlineAvailability ? 2 : 0) + (b.inStoreAvailability ? 1 : 0);
                        if (availabilityA !== availabilityB) {
                            return availabilityB - availabilityA;
                        }
                        return (getDisplayPrice(a) || 0) - (getDisplayPrice(b) || 0);
                    });

                setStocks(filtered);
                setLastRefreshedAt(new Date());
            } catch (loadError) {
                console.error("Failed to load track hub data:", loadError);
                setError("Unable to load tracker data right now.");
            } finally {
                setLoading(false);
            }
        };

        loadStocks();
    }, [hub]);

    const pageDescription = hub?.description || "Track GPU stock with StockMaid.";
    const pageTitle = hub?.title || "GPU Stock Tracker | StockMaid";
    const trackPath = `/track/${hub?.slug || trackSlug || ""}`;
    const canonicalUrl = `${SITE_URL}${trackPath}`;
    const pageJsonLd = hub ? {
        "@context": "https://schema.org",
        "@graph": [
            {
                "@type": "CollectionPage",
                name: pageTitle,
                description: pageDescription,
                url: canonicalUrl
            },
            {
                "@type": "BreadcrumbList",
                itemListElement: [
                    {
                        "@type": "ListItem",
                        position: 1,
                        name: "Home",
                        item: SITE_URL
                    },
                    {
                        "@type": "ListItem",
                        position: 2,
                        name: "Trackers",
                        item: `${SITE_URL}/stocks`
                    },
                    {
                        "@type": "ListItem",
                        position: 3,
                        name: hub.series,
                        item: canonicalUrl
                    }
                ]
            }
        ]
    } : null;

    const liveStatusLabel = useMemo(() => {
        if (!lastRefreshedAt) {
            return "Live status unavailable";
        }
        return `Last refreshed ${lastRefreshedAt.toLocaleString()}`;
    }, [lastRefreshedAt]);

    if (!hub) {
        return (
            <Container maxWidth="md" sx={{ py: 8 }}>
                <Seo
                    title="Tracker Not Found | StockMaid"
                    description="This tracker page does not exist."
                    canonicalPath={`/track/${trackSlug || ""}`}
                    robots="noindex,nofollow"
                />
                <Typography variant="h4" component="h1" gutterBottom>
                    Tracker Not Found
                </Typography>
                <Typography variant="body1">
                    The requested tracker does not exist.
                </Typography>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Seo
                title={pageTitle}
                description={pageDescription}
                canonicalPath={`/track/${hub.slug}`}
                jsonLd={pageJsonLd}
            />

            <Box sx={{ mb: 4 }}>
                <Typography variant="h3" component="h1" gutterBottom>
                    {hub.series} Stock Tracker
                </Typography>
                <Typography variant="body1" sx={{ maxWidth: 900, mb: 2 }}>
                    {hub.intro}
                </Typography>
                <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                    <Chip label="Best Buy focused" color="primary" />
                    <Chip label={liveStatusLabel} variant="outlined" />
                </Box>
            </Box>

            <Box sx={{ display: "flex", gap: 2, mb: 4, flexWrap: "wrap" }}>
                <Button variant="contained" component={Link} to="/subscription">
                    Get Alert
                </Button>
                <Button variant="outlined" component={Link} to="/stocks">
                    Browse All Tracked GPUs
                </Button>
            </Box>

            {loading ? (
                <Box sx={{ py: 8, display: "flex", justifyContent: "center" }}>
                    <CircularProgress />
                </Box>
            ) : error ? (
                <Typography color="error">{error}</Typography>
            ) : (
                <Grid container spacing={2}>
                    {stocks.map((stock) => (
                        <Grid item xs={12} md={6} key={stock.sku}>
                            <Card variant="outlined">
                                <CardContent>
                                    <Typography variant="h6" component="h2" sx={{ mb: 1 }}>
                                        {stock.name}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                        SKU: {stock.sku}
                                    </Typography>
                                    <Typography variant="body1" sx={{ mb: 1 }}>
                                        {formatCurrency(getDisplayPrice(stock)) || "Price unavailable"}
                                    </Typography>
                                    {stock.onSale && (
                                        <Typography variant="body2" sx={{ mb: 1, color: "var(--sm-success)", fontWeight: 700 }}>
                                            Save {formatCurrency(stock.dollarSavings) || ""}{stock.percentSavings ? ` (${formatPercent(stock.percentSavings)} off)` : ""}
                                        </Typography>
                                    )}
                                    <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
                                        <Chip
                                            size="small"
                                            color={stock.onlineAvailability ? "success" : "default"}
                                            label={stock.onlineAvailability ? "Online In Stock" : "Online Out of Stock"}
                                        />
                                        <Chip
                                            size="small"
                                            color={stock.inStoreAvailability ? "success" : "default"}
                                            label={stock.inStoreAvailability ? "Pickup In Stock" : "Pickup Out of Stock"}
                                        />
                                    </Box>
                                    <Button component={Link} to={`/skus/${stock.sku}`} size="small">
                                        View SKU Details
                                    </Button>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            )}
        </Container>
    );
};
